import React, { FC, useEffect, useRef } from "react";
import styled from "styled-components";
import { useIsDarkMode } from "@hooks/useIsDarkMode";
import gsap, { Circ } from "gsap";
import { breakpoints } from "@theme/breakpoints";

const StyledWrapper = styled.button<{ $background: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 10px;
  box-shadow: 0 0 54px -11px rgba(104, 89, 71, 0.5);
  border: 0;
  cursor: pointer;
  background: ${({ theme, $background }) => $background || theme.background};
  transition: background 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  :hover {
    box-shadow: 0 0 54px -11px rgba(104, 89, 71, 1);
  }

  :active {
    transform: scale(0.9);
    transition: transform 0.4s cubic-bezier(0.33, 1, 0.68, 1);
  }

  ${breakpoints.tabletMinMax`
    width: 60px;
    height: 60px;
  `}
`;

const StyledInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 24px;
  height: 20px;
  justify-content: space-between;

  span {
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: ${({ theme }) => theme.primary};
    display: block;
    will-change: width;

    :first-child,
    :last-child {
      width: 50%;
      transform-origin: left top;
    }

    :last-child {
      border-bottom: 0;
      margin-left: auto;
    }
  }
`;

export const Hamburger: FC<Props> = ({
  toggled,
  onToggle,
  children,
  ...props
}) => {
  const isDarkMode = useIsDarkMode();
  const background = isDarkMode ? "#3a3a3a" : "#fff";
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const allSpans = wrapper.current?.querySelectorAll("span");
    const tl = gsap.timeline();

    const y = 8;

    if (toggled && allSpans) {
      tl.to([allSpans[0], allSpans[2]], {
        width: "100%",
        duration: 0.35,
        ease: Circ.easeInOut,
      })
        .to(allSpans[1], {
          scaleX: 0,
          duration: 0.35,
          delay: -0.35,
          ease: Circ.easeInOut,
        })
        .to(allSpans[0], {
          y,
          duration: 0.35,
          rotate: 45,
          transformOrigin: "center center",
          ease: Circ.easeInOut,
        })
        .to(allSpans[2], {
          y: -y,
          duration: 0.35,
          delay: -0.35,
          rotate: -45,
          transformOrigin: "center center",
          ease: Circ.easeInOut,
        });
    } else if (!toggled && allSpans) {
      tl.to([allSpans[0], allSpans[2]], {
        rotate: 0,
        duration: 0.35,
        x: 0,
        y: 0,
        ease: Circ.easeInOut,
      })
        .set(allSpans[0], { transformOrigin: "left top" })
        .set(allSpans[2], { transformOrigin: "right top" })
        .to([allSpans[0], allSpans[2]], {
          width: "50%",
          duration: 0.35,
          ease: Circ.easeInOut,
        })
        .to(allSpans[1], {
          scaleX: 1,
          duration: 0.35,
          ease: Circ.easeInOut,
          delay: -0.35,
        });
    }

    return () => {
      tl.kill();
    };
  }, [toggled]);

  return (
    <StyledWrapper
      onClick={() => onToggle((prevState) => !prevState)}
      $background={background}
      {...props}
    >
      <StyledInnerWrapper ref={wrapper}>
        <span />
        <span />
        <span />
      </StyledInnerWrapper>
    </StyledWrapper>
  );
};

interface Props {
  toggled: boolean;
  onToggle: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
  title?: string;
}
